// ================================
// IMPORTS
// ================================
import { type Ref, inject } from "vue"
import { useTaskStore } from "@core/stores/task.store"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"

// ================================
// TYPES
// ================================
export type CreateIotAnalysePayload = {
    dateFrom: string
    dateTo: string
    tempUtilisation: number
    tempNoUtilisation: number
}

export type CreateIotAnalyseResult =
    | { status: "success" }
    | { status: "error"; error: string }

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Composable pour créer une analyse IoT (température ambiance) et lancer le suivi de la tâche.
 * S'appuie sur useFetchWrapper ; le parent (ex. CreateAnalyseIotDialog) écoute et émet un refetch
 * pour rafraîchir la liste des analyses.
 *
 * @param buildingId - Ref sur l'identifiant du bâtiment
 * @param onRefetch - Callback appelé après création et après succès de la tâche (le parent émet alors un refetch)
 */
export function useCreateIotAnalyse(
    buildingId: Ref<string | undefined>,
    onRefetch?: () => void
) {
    const taskStore = useTaskStore()
    const services = inject<Services>("services")

    const wrapper = useFetchWrapper(async (kwargs: unknown) => {
        const payload = kwargs as CreateIotAnalysePayload
        const id = buildingId.value
        if (!id) throw new Error("Bâtiment non trouvé")
        const createApi = services?.$createIotAnalyse
        if (!createApi) {
            throw new Error("Service de création d'analyse non disponible")
        }
        const result = await createApi({
            buildingId: id,
            dateFrom: payload.dateFrom,
            dateTo: payload.dateTo,
            tempUtilisation: payload.tempUtilisation,
            tempNoUtilisation: payload.tempNoUtilisation,
        })
        await onRefetch?.()
        taskStore.followEvent(result.task_id, result.event_name, {
            taskname: "analyse.iot",
            taskToastTitle: "Analyse température ambiance en cours...",
            notification: true,
            autoDelete: true,
            onSuccess: () => {
                onRefetch?.()
            },
        })
    })

    return {
        fetch: wrapper.fetch,
        isFetching: wrapper.isFetching,
        hasError: wrapper.hasError,
        isSuccess: wrapper.isSuccess,
        error: wrapper.error,
        cancel: wrapper.cancel,
    }
}
